import { NavigationDropdown } from '@ardoq/dropdown-menu';
import { EnhancedScopeDataWithBranchName } from '../../scopeData/enhanceScopeData';
import { readRawValue } from '../../scopeData/utils/readRawValue';
import {
  EntityDataCorruptionType,
  getDataCorruptionDescription,
  getEntityDataCorruptionType,
} from '../../scopeData/utils/dataCorruption';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { withWorkspacePopover } from '../../DiffMergeTablePopover/utils';
import { Spacer } from '../../scopeData/atoms';
import { AlertIcon } from '../iconAndHintMessageRenderer';
import { EntityRepresentationProps } from './EntityRepresentationProps';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { IconSize } from '@ardoq/icons';
import { DecoratorPlaceholder } from '@ardoq/decorators';
import { ComponentRepresentationWithLink } from './ComponentRepresentationWithLink';
import { EntityName, FlexWrapper, IconContainer, Name } from './atoms';
import { ReferenceRepresentation } from './ReferenceRepresentation';
import { ComponentTypeRepresentation } from './ComponentTypeRepresentation';
import { ReferenceTypeRepresentation } from './ReferenceTypeRepresentation';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isEnhancedScopeDataWithBranchName } from './utils';
import { isEmpty, noop } from 'lodash';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const ModelRepresentation = ({
  entityId,
  enhancedScopeData,
  isCommaSeparated,
  hasLabelEllipsis,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  entityId: ArdoqId;
  enhancedScopeData: EnhancedScopeData;
  isCommaSeparated?: boolean;
  hasLabelEllipsis?: boolean;
  i18nLocale?: I18nLocale;
}) => {
  const workspace = enhancedScopeData.workspaces.find(
    ({ componentModel }) => componentModel === entityId
  );
  if (!workspace) {
    logError(Error('Missing workspace for model'));
    return null;
  }
  const modelSuffix = t(i18nLocale, 'renderers:modelSuffix', '(Model)');
  return (
    <Name
      $isCommaSeparated={isCommaSeparated}
      $withEllipsis={hasLabelEllipsis}
    >{`${workspace.name} ${modelSuffix}`}</Name>
  );
};

const FieldRepresentation = ({
  entityId,
  enhancedScopeData,
}: {
  entityId: ArdoqId;
  enhancedScopeData: EnhancedScopeDataWithBranchName | EnhancedScopeData;
}) => {
  const workspacePopover = isEnhancedScopeDataWithBranchName(enhancedScopeData)
    ? withWorkspacePopover({
        branch: enhancedScopeData.branchName,
        entityId,
        entityType: APIEntityType.FIELD,
      })
    : {};
  return (
    <Name {...workspacePopover}>
      {readRawValue(APIEntityType.FIELD, entityId, 'label', enhancedScopeData)}
    </Name>
  );
};

const EntityDataCorrupted = ({
  name,
  entityDataCorruptionType,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  name?: string;
  entityDataCorruptionType: EntityDataCorruptionType;
  i18nLocale?: I18nLocale;
}) => {
  const description = getDataCorruptionDescription(
    entityDataCorruptionType,
    i18nLocale
  );
  return (
    <WithUncontrolledPopover
      content={
        description ? <PlainTextPopover>{description}</PlainTextPopover> : null
      }
    >
      <FlexWrapper>
        <IconContainer>
          <AlertIcon iconSize={IconSize.SMALL} />
        </IconContainer>
        <Spacer />
        <DecoratorPlaceholder>
          {name || t(i18nLocale, 'renderers:unknown', 'Unknown')}
        </DecoratorPlaceholder>
      </FlexWrapper>
    </WithUncontrolledPopover>
  );
};

export const EntityRepresentation = ({
  entityId,
  parentEntityId,
  entityType,
  enhancedScopeData,
  graphics,
  isCommaSeparated,
  hasLabelEllipsis,
  hideDefaultPopover,
  shouldHaveWrapper,
  componentLinkHandler,
  navigationOptions,
  underlineOnHover,
  linkColor,
  variant,
  i18nLocale,
}: EntityRepresentationProps) => {
  const entityDataCorruptionType = getEntityDataCorruptionType({
    entityId,
    entityType,
    parentEntityId,
    enhancedScopeData,
  });
  if (entityDataCorruptionType !== EntityDataCorruptionType.NONE) {
    const name = readRawValue(
      entityType,
      entityId,
      'name',
      enhancedScopeData,
      parentEntityId
    );
    return (
      <EntityDataCorrupted
        name={name}
        entityDataCorruptionType={entityDataCorruptionType}
        i18nLocale={i18nLocale}
      />
    );
  } else if (entityType === APIEntityType.TAG) {
    return (
      <EntityName
        enhancedScopeData={enhancedScopeData}
        entityId={entityId}
        entityType={entityType}
        isCommaSeparated={isCommaSeparated}
        hasLabelEllipsis={hasLabelEllipsis}
        hideDefaultPopover={hideDefaultPopover}
        popoverAttributes={
          isEnhancedScopeDataWithBranchName(enhancedScopeData)
            ? withWorkspacePopover({
                branch: enhancedScopeData.branchName,
                entityId,
                entityType: APIEntityType.TAG,
              })
            : undefined
        }
      />
    );
  } else if (entityType === APIEntityType.COMPONENT) {
    if (navigationOptions && !isEmpty(navigationOptions)) {
      return (
        <NavigationDropdown
          navigationOptions={navigationOptions}
          WrapperComponent={FlexWrapper}
        >
          <ComponentRepresentationWithLink
            entityId={entityId as string}
            enhancedScopeData={enhancedScopeData}
            graphics={graphics}
            isCommaSeparated={isCommaSeparated}
            hasLabelEllipsis={hasLabelEllipsis}
            hideDefaultPopover={hideDefaultPopover}
            shouldHaveWrapper={shouldHaveWrapper}
            underlineOnHover={underlineOnHover}
            componentLinkHandler={{
              href: '',
              onClick: noop,
              tooltipText: '',
            }}
          />
        </NavigationDropdown>
      );
    }
    return (
      <ComponentRepresentationWithLink
        entityId={entityId as string}
        enhancedScopeData={enhancedScopeData}
        graphics={graphics}
        isCommaSeparated={isCommaSeparated}
        hasLabelEllipsis={hasLabelEllipsis}
        hideDefaultPopover={hideDefaultPopover}
        shouldHaveWrapper={shouldHaveWrapper}
        componentLinkHandler={componentLinkHandler}
        underlineOnHover={underlineOnHover}
        linkColor={linkColor}
        variant={variant}
      />
    );
  } else if (entityType === APIEntityType.REFERENCE) {
    return (
      <ReferenceRepresentation
        referenceId={entityId as string}
        enhancedScopeData={enhancedScopeData}
        graphics={graphics}
        hideDefaultPopover={hideDefaultPopover}
        isCommaSeparated={isCommaSeparated}
      />
    );
  } else if (entityType === APIEntityType.COMPONENT_TYPE) {
    return (
      <ComponentTypeRepresentation
        entityId={entityId as string}
        parentEntityId={parentEntityId!}
        enhancedScopeData={enhancedScopeData}
        graphics={graphics}
        isCommaSeparated={isCommaSeparated}
        hasLabelEllipsis={hasLabelEllipsis}
        hideDefaultPopover={hideDefaultPopover}
      />
    );
  } else if (entityType === APIEntityType.FIELD) {
    return (
      <FieldRepresentation
        entityId={entityId as string}
        enhancedScopeData={enhancedScopeData}
      />
    );
  } else if (entityType === APIEntityType.REFERENCE_TYPE) {
    return (
      <ReferenceTypeRepresentation
        entityId={entityId as number}
        parentEntityId={parentEntityId!}
        enhancedScopeData={enhancedScopeData}
        isCommaSeparated={isCommaSeparated}
        hasLabelEllipsis={hasLabelEllipsis}
        hideDefaultPopover={hideDefaultPopover}
        shouldHaveWrapper={shouldHaveWrapper}
      />
    );
  } else if (entityType === APIEntityType.MODEL) {
    return (
      <ModelRepresentation
        entityId={entityId as string}
        enhancedScopeData={enhancedScopeData}
        isCommaSeparated={isCommaSeparated}
        hasLabelEllipsis={hasLabelEllipsis}
        i18nLocale={i18nLocale}
      />
    );
  }
  return null;
};
